// Reactを持ってくる{操作後の指示,履歴保存}Reactから
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

// 日本語のページにしますよ
import { jaJPText } from "../stores/Mui_JapanesTxist";

// MUIの{表、列、差往査のためのもの}を持ってくる
import {
  DataGridPro,
  GridColDef,
  useGridApiRef,
} from "@mui/x-data-grid-pro";

// MUIの画面の見た目の部品{レイアウト用の箱,入力ボックス,アイコン}を持ってくる
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Link,
  Tooltip,
  Stack,
} from "@mui/material";

// ログイン情報管理
// Recoil=Reactの状態管理ライブラリ、Atom=Stateを管理
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";

// HTTP通信(API通信)でサーバーからデータを取得する
import axios from "axios";

// よく使う定数を管理
import ConstList from "../utils/ConstList";

// ロードアイコン=処理中です=CircularProgress
import CircularProgress from "@mui/material/CircularProgress";

// 検索マークのアイコン
import SearchIcon from "@mui/icons-material/Search";

// 登録ボタンアイコン
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

// ページコンポーネント
import ProsCustomerAddPage from "./Pros_customer_Add";
import ProsCustomerEditPage from "./Pros_customer_Edit";
import ProsCustomerDeletePage from "./Pros_customer_Delete";
import CustomerProjectDetailDialog from "./CustomerProjectDetailDialog";

// デザインのテーマを適用
import { designTokens } from "../theme";

type ProspectiveCustomerRow = {
  id: number;
  Company_id: number;
  Company_name: string;
  Lead_sources_id: number | "";
  Lead_sources: string;
  Business_description: string;
  Company_telephone: string;
  Company_mail: string;
  Company_address: string;
  Company_website: string;
  Sales_status_id: number | "";
  Sales_status: string;
  is_active?: boolean;
};

const colors = {
  bg: designTokens.surface.page,
  panel: designTokens.surface.paper,
  panel2: designTokens.surface.subtle,
  panelHover: designTokens.overlay.brandSoft,
  header: designTokens.surface.subtle,
  text: designTokens.text.primary,
  muted: designTokens.text.secondary,
  accent: designTokens.brand.dark,
  accentSoft: designTokens.overlay.brandSoft,
  accentBorder: designTokens.overlay.brandMedium,
  danger: designTokens.state.error,
  dangerSoft: "rgba(220, 90, 82, 0.10)",
  border: designTokens.border.default,
};

const normalizeText = (text: string | number | null | undefined) => {
  if (text === null || text === undefined) return "";
  return String(text).normalize("NFKC").toLowerCase();
};

const normalizeUrl = (url: string | null | undefined) => {
  const value = String(url ?? "").trim();

  if (!value) return "";

  if (value.startsWith("http://") || value.startsWith("https://")) {
    return value;
  }

  return `https://${value}`;
};


const toOptionalNumber = (value: unknown): number | "" => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const numericValue = Number(value);

  return Number.isNaN(numericValue) ? "" : numericValue;
};

// 色を使いすぎないため、基本は同系色で統一。
// 「商談進行中」など動きがある状態だけアクセントを付ける。
const getStatusChipStyle = (statusLabelRaw: any) => {
  const label = normalizeText(statusLabelRaw);

  if (label.includes("商談") || label.includes("進行")) {
    return {
      bg: colors.accentSoft,
      fg: colors.accent,
      border: `1px solid ${colors.accentBorder}`,
      fontWeight: 900,
    };
  }

  return {
    bg: designTokens.surface.subtle,
    fg: colors.muted,
    border: `1px solid ${colors.border}`,
    fontWeight: 700,
  };
};

// ##############################################################################
// ############################## Gridのカラム設定 #############################
// ##############################################################################

const GridColumns = (
  onEditClick: (row: ProspectiveCustomerRow) => void,
  onDeleteClick: (row: ProspectiveCustomerRow) => void,
  onReportClick: (row: ProspectiveCustomerRow) => void,
  DisPlayButton: boolean,
  LoginId: string,
): GridColDef<ProspectiveCustomerRow>[] => {
  const columns: GridColDef<ProspectiveCustomerRow>[] = [];

  // ✅ 権限があるときだけ「操作」列を追加
  if (LoginId !== "") {
    columns.push({
      field: "action",
      headerName: "操作",
      minWidth: 300,
      sortable: false,
      filterable: false,
      headerClassName: "custom-header-important",
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            size="small"
            disabled={DisPlayButton}
            sx={{
              backgroundColor: colors.accent,
              color: "#FFFFFF",
              fontWeight: 800,
              "&:hover": {
                backgroundColor: designTokens.brand.light,
                color: "#FFFFFF",
              },
            }}
            onClick={() => onEditClick(params.row)}
          >
            変更
          </Button>

          <Button
            variant="outlined"
            size="small"
            disabled={DisPlayButton}
            onClick={() => onReportClick(params.row)}
          >
            日報
          </Button>

          <Button
            variant="outlined"
            size="small"
            disabled={DisPlayButton}
            sx={{
              color: colors.danger,
              borderColor: "rgba(220,90,82,0.34)",
              fontWeight: 800,
              "&:hover": {
                borderColor: colors.danger,
                backgroundColor: colors.dangerSoft,
              },
            }}
            onClick={() => onDeleteClick(params.row)}
          >
            使用停止
          </Button>
        </Box>
      ),
    });
  }

  columns.push(
    {
      field: "Company_name",
      headerName: "企業名",
      flex: 1.15,
      minWidth: 190,
      headerClassName: "custom-header-important",
      renderCell: (params) => (
        <Box
          sx={{
            color: colors.text,
            fontWeight: 800,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {params.value || "-"}
        </Box>
      ),
    },
    {
      field: "Sales_status",
      headerName: "アプローチ状況",
      flex: 0.85,
      minWidth: 170,
      headerClassName: "custom-header-important",
      renderCell: (params) => {
        const style = getStatusChipStyle(params.row?.Sales_status);
        const label = String(params.value ?? "不明");

        return (
          <Tooltip title={label}>
            <Chip
              label={label}
              size="small"
              sx={{
                width: 118,
                maxWidth: 118,
                height: 28,
                justifyContent: "center",
                bgcolor: style.bg,
                color: style.fg,
                border: style.border,
                fontWeight: style.fontWeight,
                borderRadius: "9999px",
                "& .MuiChip-label": {
                  display: "block",
                  width: "100%",
                  px: 1,
                  textAlign: "center",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                },
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "Lead_sources",
      headerName: "営業に至ったきっかけ",
      flex: 0.9,
      minWidth: 170,
      headerClassName: "custom-header-important",
    },
    {
      field: "Business_description",
      headerName: "業種",
      flex: 0.75,
      minWidth: 130,
      headerClassName: "custom-header-important",
    },
    {
      field: "Company_telephone",
      headerName: "電話番号",
      flex: 0.75,
      minWidth: 130,
      headerClassName: "custom-header-important",
    },
    {
      field: "Company_mail",
      headerName: "メールアドレス",
      flex: 1,
      minWidth: 180,
      headerClassName: "custom-header-important",
      renderCell: (params) => {
        const mail = String(params.value ?? "").trim();

        if (!mail) return <Box sx={{ color: colors.muted }}>-</Box>;

        return (
          <Link
            href={`mailto:${mail}`}
            underline="hover"
            sx={{
              color: colors.text,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              "&:hover": {
                color: colors.accent,
              },
            }}
          >
            {mail}
          </Link>
        );
      },
    },
    {
      field: "Company_website",
      headerName: "URL",
      flex: 0.75,
      minWidth: 90,
      align: "center",
      headerAlign: "center",
      sortable: false,
      headerClassName: "custom-header-important",
      renderCell: (params) => {
        const rawUrl = String(params.value ?? "").trim();
        const url = normalizeUrl(rawUrl);

        if (!url) {
          return <Box sx={{ color: colors.muted }}>-</Box>;
        }

        return (
          <Tooltip title={rawUrl || "登録URLを開く"}>
            <IconButton
              component="a"
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              size="small"
              aria-label="登録URLを開く"
              sx={{
                width: 32,
                height: 32,
                color: colors.accent,
                border: `1px solid ${colors.accentBorder}`,
                backgroundColor: designTokens.overlay.brandSoft,
                "&:hover": {
                  backgroundColor: colors.accentSoft,
                  borderColor: colors.accent,
                },
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <OpenInNewIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: "Company_address",
      headerName: "会社住所",
      flex: 1.1,
      minWidth: 180,
      headerClassName: "custom-header-important",
    },
  );

  return columns;
};

const MasterProsCustomers = () => {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [selectedRow, setSelectedRow] = useState<ProspectiveCustomerRow | null>(
    null,
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [ProspectiveCustomerData, setProspectiveCustomerData] = useState<
    ProspectiveCustomerRow[]
  >([]);
  const [leadSourceList, setLeadSourceList] = useState<any[]>([]);
  const [salesStatusList, setSalesStatusList] = useState<any[]>([]);

  // ポップアップ設定
  const [openSubscribe, setopenSubscribe] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  // 新規登録ダイアログの入力内容リセット用
  const [addDialogKey, setAddDialogKey] = useState(0);

  // 画面再表示
  const [ReView, setReView] = useState(false);

  // 使用停止直後に、親画面から即時に除外するためのローカル保持
  // ※本来は ProspectiveCustomers.php 側で is_active=false を除外するのが正です。
  const [stoppedCompanyIds, setStoppedCompanyIds] = useState<Set<number>>(
    new Set(),
  );

  // 検索機能で使用する
  const [searchText, setSearchText] = useState<string>("");

  // 現在のGridの中身を把握するのに必要
  const apiRef = useGridApiRef();

  // 並び変えのボタン表示の設定
  const [rowReordering, setRowReordering] = useState(false);
  const [reorderedData, setReorderedData] = useState<ProspectiveCustomerRow[]>(
    [],
  );

  // ##############################################################################
  // #################### Master_見込み顧客API #############################
  // ##############################################################################

  const fetchData = async () => {
    setLoading(true);

    const postData = {
      flag: "Prospective_Customers",
      access_token: loginUserData.accessToken,
      algorithm: loginUserData.algorithm,
    };

    try {
      const response = await axios.post(
        ConstList.getApiUrl() + "ProspectiveCustomers.php",
        postData,
        ConstList.AXIOS_CONFIG,
      );

      if (response.data.data === false) {
        setErrorMessage(response.data.message);

        if (response.data.reDirect !== "") {
          await ConstList.SLEEP(5000);
          window.location.href = response.data.reDirect;
        }
      } else {
        const rawCustomerList: any[] = response.data.data.mastercustomer ?? [];

        const nextData: ProspectiveCustomerRow[] = rawCustomerList
          .filter((value: any) => {
            const companyId = Number(value.company_id);

            // 使用停止API成功直後は、再取得結果に残っていても親画面から除外する。
            if (stoppedCompanyIds.has(companyId)) {
              return false;
            }

            // PHP側が is_active / Is_active / active を返している場合はここで除外する。
            const activeValue =
              value.is_active ?? value.Is_active ?? value.active ?? true;

            if (
              activeValue === false ||
              activeValue === "false" ||
              activeValue === 0 ||
              activeValue === "0"
            ) {
              return false;
            }

            return true;
          })
          .map((value: any): ProspectiveCustomerRow => ({
            // DataGridのidは会社IDに寄せる。
            // index + 1 だと再取得・並び替え時に行の同一性がズレやすい。
            id: Number(value.company_id),

            // 数値化しておくことで、内部処理や初期表示順が安定する。
            Company_id: Number(value.company_id),
            Company_name: value.company_name ?? "",
            Lead_sources_id: toOptionalNumber(value.lead_sources_id),
            Lead_sources: value.lead_sources ?? "",
            Business_description: value.business_description ?? "",
            Company_telephone: value.company_telephone ?? "",
            Company_mail: value.company_mail ?? "",
            Company_address: value.company_address ?? "",
            Company_website: value.company_website ?? "",
            Sales_status_id: toOptionalNumber(value.sales_status_id),
            Sales_status: value.sales_status ?? "未設定",
            is_active:
              value.is_active === undefined
                ? true
                : value.is_active === true || value.is_active === "true",
          }))
          .sort((a, b) => a.Company_id - b.Company_id);

        setProspectiveCustomerData(nextData);
        setLeadSourceList(response.data.data.lead_sources_list || []);
        setSalesStatusList(response.data.data.sales_status_list || []);
        setErrorMessage(null);
      }
    } catch (error) {
      console.error("データ取得エラー:", error);
      setErrorMessage("データの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  };

  // マウント時にデータを取得
  useEffect(() => {
    fetchData();
  }, []);

  // ##############################################################################
  // ######################### ボタン押した際の処理 ###############################
  // ##############################################################################

  const handleSubscribe = () => {
    // 新規登録を開くたびにコンポーネントを作り直して入力内容を初期化する
    setAddDialogKey((prev) => prev + 1);
    setopenSubscribe(true);
  };

  const handleEdit = (row: ProspectiveCustomerRow) => {
    setSelectedRow(row);
    setOpenEdit(true);
  };

  const handleDelete = (row: ProspectiveCustomerRow) => {
    setSelectedRow(row);
    setOpenDelete(true);
  };

  const handleDeleted = (companyId: number) => {
    if (!companyId) return;

    setStoppedCompanyIds((prev) => {
      const next = new Set(prev);
      next.add(companyId);
      return next;
    });

    // 画面上から即時に対象行を除外
    setProspectiveCustomerData((prev) =>
      prev.filter((row) => row.Company_id !== companyId),
    );
    setReorderedData((prev) =>
      prev.filter((row) => row.Company_id !== companyId),
    );
  };

  // ##############################################################################
  // #################### 各ボタンから戻ったとき画面更新 ###########################
  // ##############################################################################

  useEffect(() => {
    if (ReView === true) {
      setReView(false);

      // 新規登録・編集・使用停止後はダイアログを閉じる
      setopenSubscribe(false);
      setOpenEdit(false);
      setOpenDelete(false);
      setSelectedRow(null);

      // 次回開いたときの入力内容を必ず初期化
      setAddDialogKey((prev) => prev + 1);

      fetchData();
    }
  }, [ReView]);

  // ##############################################################################
  // ####################### 検索機能 ######################################
  // ##############################################################################

  const filteredData = useMemo(() => {
    const keyword = normalizeText(searchText);

    if (!keyword) return ProspectiveCustomerData;

    return ProspectiveCustomerData.filter((row) => {
      return (
        normalizeText(row.Company_name).includes(keyword) ||
        normalizeText(row.Company_id).includes(keyword) ||
        normalizeText(row.Lead_sources).includes(keyword) ||
        normalizeText(row.Business_description).includes(keyword) ||
        normalizeText(row.Company_telephone).includes(keyword) ||
        normalizeText(row.Company_mail).includes(keyword) ||
        normalizeText(row.Company_website).includes(keyword) ||
        normalizeText(row.Company_address).includes(keyword) ||
        normalizeText(row.Sales_status).includes(keyword)
      );
    });
  }, [ProspectiveCustomerData, searchText]);

  // ##############################################################################
  // #################### 並び替え機能 ######################################
  // ##############################################################################

  useEffect(() => {
    if (rowReordering) {
      const rowIds = apiRef.current.getAllRowIds();

      const nextReorderedData = rowIds
        .map((id) => filteredData.find((row) => row.id === id))
        .filter((row): row is ProspectiveCustomerRow => Boolean(row));

      setReorderedData(nextReorderedData);
    } else {
      setReorderedData(filteredData);
    }
  }, [rowReordering, filteredData, apiRef]);

  const displayRows = rowReordering ? reorderedData : filteredData;

  const columns = useMemo(
    () =>
      GridColumns(
        handleEdit,
        handleDelete,
        (row) => navigate(`/sales-reports?view=create&customerType=prospective&companyId=${row.Company_id}`),
        rowReordering,
        loginUserData.userId,
      ),
    [navigate, rowReordering, loginUserData.userId],
  );

  // ##############################################################################
  // #################### 実際のコンポーネント表示 #################################
  // ##############################################################################

  return (
      <Box
        sx={{
          height: "70vh",
          color: colors.text,
          backgroundColor: colors.bg,
        }}
      >
        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
        >
          <TextField
            label="企業名称で検索"
            variant="outlined"
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: colors.muted }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: "280px",

              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.border,
              },

              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: colors.panel,
                boxShadow: "0 1px 2px rgba(15,23,42,0.04)",

                "&:hover": {
                  backgroundColor: colors.panel,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: designTokens.border.strong,
                  },
                },
                "&.Mui-focused": {
                  backgroundColor: colors.panel,
                  boxShadow: "0 0 0 3px rgba(152,188,46,0.16)",
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                },
              },

              "& .MuiInputBase-input": { color: colors.text },
              "& .MuiInputBase-input::placeholder": {
                color: colors.muted,
                opacity: 1,
              },
              "& .MuiInputLabel-root": { color: colors.muted },
              "& .MuiInputLabel-root.Mui-focused": { color: colors.accent },
            }}
          />

          {loginUserData.userId !== "" && (
            <Stack direction="row" alignItems="center" gap={1.5}>
              <Button
                variant="contained"
                size="large"
                disabled={rowReordering}
                sx={{
                  backgroundColor: colors.accent,
                  color: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: "bold",
                  borderRadius: "8px",
                  padding: "8px 16px",
                  boxShadow: "0 6px 16px rgba(15,23,42,0.10)",
                  "&:hover": {
                    backgroundColor: designTokens.brand.primary,
                    color: "#FFFFFF",
                  },
                }}
                onClick={handleSubscribe}
                startIcon={<AddIcon />}
              >
                新規登録
              </Button>
            </Stack>
          )}
        </Box>

        {errorMessage && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1.1,
              color: colors.danger,
              backgroundColor: colors.dangerSoft,
              border: "1px solid rgba(220,90,82,0.24)",
              borderRadius: 2,
              fontWeight: 700,
            }}
          >
            {errorMessage}
          </Box>
        )}

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "200px",
              color: colors.text,
            }}
          >
            <CircularProgress />
            <Box className="mt-2 text-sm text-gray-500" sx={{ ml: 2 }}>
              読み込み中...
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              height: "62vh",
              width: "100%",
              overflow: "hidden",
              borderRadius: 3,
              backgroundColor: colors.panel,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(15,23,42,0.06)",
            }}
          >
            <DataGridPro
              apiRef={apiRef}
              rows={displayRows}
              columns={columns}
              localeText={jaJPText}
              loading={loading}
              autoPageSize
              rowReordering={rowReordering}
              disableRowSelectionOnClick
              onRowDoubleClick={(params) => {
                setSelectedRow(params.row);
                setDetailOpen(true);
              }}
              sx={{
                border: "none",
                color: colors.text,
                backgroundColor: colors.panel,

                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: colors.header,
                  color: colors.muted,
                  borderBottom: `1px solid ${colors.border}`,
                },
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 900,
                },
                "& .MuiDataGrid-columnSeparator": {
                  color: colors.muted,
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: `1px solid ${colors.border}`,
                  outline: "none !important",
                  fontWeight: 400,
                  "& .MuiTypography-root": { fontWeight: 400 },
                  "& .MuiBox-root": { fontWeight: 800 },
                },
                "& .MuiDataGrid-row": {
                  backgroundColor: colors.panel,
                },
                "& .MuiDataGrid-row.Mui-selected": {
                  backgroundColor: colors.accentSoft,
                  "&:hover": {
                    backgroundColor: "rgba(152,188,46,0.16)",
                  },
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: colors.panelHover,
                },
                "& .MuiDataGrid-footerContainer": {
                  color: colors.muted,
                  backgroundColor: colors.header,
                  borderTop: `1px solid ${colors.border}`,
                },
                "& .MuiTablePagination-root": {
                  color: colors.muted,
                },
                "& .MuiDataGrid-overlay": {
                  color: colors.muted,
                  backgroundColor: colors.panel,
                },
              }}
            />
          </Box>
        )}

        {/* ✅：新規登録用・モーダルウィンドウ */}
        {openSubscribe && (
          <ProsCustomerAddPage
            key={addDialogKey}
            AddOpen={openSubscribe}
            SetAddOpen={setopenSubscribe}
            ReViewPage={setReView}
            leadSourceList={leadSourceList}
          />
        )}

        {/* ✅：変更用・モーダルウィンドウ */}
        {selectedRow && (
          <ProsCustomerEditPage
            Data={selectedRow}
            EditOpen={openEdit}
            SetEditOpen={setOpenEdit}
            ReViewPage={setReView}
            leadSourceList={leadSourceList}
            salesStatusList={salesStatusList}
          />
        )}

        {/* ✅：削除用・モーダルウィンドウ */}
        {selectedRow && (
          <ProsCustomerDeletePage
            Data={selectedRow}
            DeleteOpen={openDelete}
            SetDeleteOpen={setOpenDelete}
            ReViewPage={setReView}
            onDeleted={handleDeleted}
          />
        )}

        <CustomerProjectDetailDialog
          open={detailOpen}
          customer={
            selectedRow
              ? {
                  company_id: selectedRow.Company_id,
                  company_name: selectedRow.Company_name,
                }
              : null
          }
          onClose={() => setDetailOpen(false)}
        />
      </Box>
  );
};

export default MasterProsCustomers;
